import {
  COLOR_DANGER,
  COLOR_DANGER_TEXT,
  COLOR_INFO,
  LGM_LINE,
} from '../../theme/colors';
import {
  GRAY_100,
  GRAY_200,
  GRAY_400,
  GRAY_50,
  GRAY_500,
  GRAY_700,
} from 'theme/grayScale';

export const modalStyles: Record<string, React.CSSProperties> = {
  modalOverlayStyle: {
    position: 'fixed',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    backgroundColor: 'rgba(0, 0, 0, 0.5)', // bg-black bg-opacity-50
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000, // Increased to ensure it appears above Leaflet map
  },
  modalTitleStyle: {
    fontSize: '18px', // text-lg
    fontWeight: 'bold', // font-bold
    marginBottom: '16px', // mb-4
  },
  modalContentStyle: {
    backgroundColor: '#ffffff',
    padding: '24px', // p-6
    borderRadius: '8px', // rounded-lg
    boxShadow:
      '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)', // shadow-lg
    width: '850px', // Wide enough for other shared consumers (GradeModal, FacilityModal, TherapeuticAreaModal, GenericMergeModal) that don't need the extra Product/Manufacturer width below
    maxWidth: '95vw', // Prevent overflow on smaller screens
    maxHeight: '90vh', // Limit height to viewport
    overflowY: 'auto', // Add scroll for tall content
  },
  floatingFieldContainer: {
    position: 'relative',
    marginTop: '8px',
    marginBottom: '16px',
  },
  textareaContainer: {
    position: 'relative',
    marginBottom: '16px',
  },
  floatingInputStyle: {
    width: '100%',
    padding: '12px 8px 8px 8px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: LGM_LINE,
    borderRadius: '4px',
    fontSize: '16px',
    outline: 'none',
    transition: 'border-color 0.2s ease-in-out',
    backgroundColor: '#ffffff',
  },
  floatingTextareaStyle: {
    width: '100%',
    padding: '12px 8px 8px 8px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: LGM_LINE,
    borderRadius: '4px',
    fontSize: '16px',
    outline: 'none',
    transition: 'border-color 0.2s ease-in-out',
    backgroundColor: '#ffffff',
    resize: 'vertical' as const,
    minHeight: '80px',
  },
  floatingLabelStyle: {
    position: 'absolute',
    left: '8px',
    top: '50%',
    transform: 'translateY(-50%)',
    fontSize: '16px',
    color: GRAY_500, // matches the label color used elsewhere in this file
    backgroundColor: '#ffffff',
    padding: '0 4px',
    transition: 'all 0.2s ease-in-out',
    pointerEvents: 'none' as const,
    zIndex: 1,
  },
  floatingLabelFocusedStyle: {
    top: '0',
    transform: 'translateY(-50%)',
    fontSize: '12px',
    color: COLOR_INFO,
    fontWeight: '500',
  },
  floatingLabelDisabledStyle: {
    color: GRAY_400,
  },
  floatingInputFocusedStyle: {
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: COLOR_INFO,
    boxShadow: `0 0 0 1px ${COLOR_INFO}`,
  },
  floatingInputDisabledStyle: {
    backgroundColor: GRAY_50,
    color: GRAY_500,
    cursor: 'not-allowed',
  },
  // Additional styles for enhanced field components
  modalHeaderStyle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
    borderBottom: `1px solid ${GRAY_200}`,
    paddingBottom: '12px',
  },
  modalBodyStyle: {
    maxHeight: '60vh',
    overflowY: 'auto' as const,
    marginBottom: '24px',
  },
  // modalBodyStyle's marginBottom exists to leave room above the footer's
  // own border/padding. Modals with no footer (header actions only) use this
  // instead — otherwise that margin stacks with modalContentStyle's own
  // bottom padding and doubles the gap below the body.
  modalBodyStyleNoFooter: {
    maxHeight: '60vh',
    overflowY: 'auto' as const,
    marginBottom: 0,
  },
  modalFooterStyle: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    paddingTop: '16px',
    borderTop: `1px solid ${GRAY_200}`,
  },
  // Fixed at 32px to match modalHeaderIconTextButtonStyle below — both sit
  // side by side in the header, so their hover/pressed backgrounds must be
  // the same height or the highlight boxes visibly mismatch on interaction.
  modalCloseXStyle: {
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: GRAY_500,
    padding: '0',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    transition: 'all 0.2s ease-in-out',
  },
  modalCloseXHoverStyle: {
    backgroundColor: GRAY_100,
    color: GRAY_700,
  },
  modalCloseXPressedStyle: {
    backgroundColor: GRAY_200, // active:bg-gray-200 (one step darker than hover)
    color: GRAY_700,
  },
  // Icon + text header Edit/Save button (LgmButton variant="flat"). Layout
  // only — resting/hover/pressed/disabled color comes from LgmButton's own
  // color-ramp system (color="primary"/"save"), so this must NOT set color
  // or backgroundColor, that would override the ramp instead of layering
  // under it. Explicit height:32px (rather than relying on LgmButton's
  // rem-based base padding) so this matches modalCloseXStyle's 32px exactly
  // — the two buttons sit side by side and must share one hover/pressed row
  // height.
  modalHeaderIconTextButtonStyle: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    height: '32px',
    padding: '0 10px',
  },
  floatingSelectStyle: {
    width: '100%',
    padding: '12px 8px 8px 8px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: LGM_LINE,
    borderRadius: '4px',
    fontSize: '16px',
    outline: 'none',
    transition: 'border-color 0.2s ease-in-out',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
  },
  floatingSelectDisabledStyle: {
    backgroundColor: GRAY_50,
    color: GRAY_500,
    cursor: 'not-allowed',
  },
  // Field validation styles
  fieldContainer: {
    margin: '8px 0 0 0',
  },
  floatingInputErrorStyle: {
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: COLOR_DANGER,
    boxShadow: `0 0 0 1px ${COLOR_DANGER}`,
  },
  floatingLabelErrorStyle: {
    color: COLOR_DANGER,
  },
  fieldErrorMessageStyle: {
    fontSize: '12px',
    color: COLOR_DANGER,
    marginTop: '4px',
    display: 'block',
  },
  fieldHelpTextStyle: {
    fontSize: '12px',
    color: GRAY_500,
    marginTop: '4px',
    display: 'block',
  },
  noConflictsContainer: {
    backgroundColor: '#ecfdf5',
    border: '1px solid #10b981',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '16px',
  },
  noConflictsTitle: {
    color: '#065f46',
    fontWeight: '500',
  },
  noConflictsDescription: {
    color: GRAY_500,
    fontSize: '14px',
    marginTop: '8px',
  },
  conflictContainer: {
    marginBottom: '16px',
  },
  conflictTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: GRAY_700,
    marginBottom: '12px',
  },
  conflictDescription: {
    fontSize: '14px',
    color: GRAY_500,
    marginBottom: '16px',
  },
  conflictFieldContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  conflictField: {
    border: `1px solid ${LGM_LINE}`,
    borderRadius: '8px',
    padding: '12px',
    backgroundColor: '#ffffff',
  },
  fieldLabel: {
    fontWeight: '600',
    fontSize: '14px',
    marginBottom: '8px',
    color: GRAY_700,
  },
  fieldOptionsContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
  },
  fieldOptionValue: {
    fontSize: '14px',
    color: GRAY_700,
    wordBreak: 'break-word',
  },
  confirmStepWarningContainer: {
    backgroundColor: '#fef2f2',
    border: `1px solid ${COLOR_DANGER}`,
    borderRadius: '4px',
    padding: '16px',
    marginBottom: '16px',
  },
  confirmStepWarningTitle: {
    marginTop: 0,
    fontSize: '14px',
    fontWeight: '600',
    color: COLOR_DANGER_TEXT,
  },
  confirmStepWarningText: {
    fontSize: '14px',
    lineHeight: '1.6',
    color: '#7f1d1d',
  },
  confirmStepChangesContainer: {
    backgroundColor: GRAY_50,
    padding: '16px',
    borderRadius: '4px',
    marginBottom: '16px',
  },
  confirmStepChangesText: {
    fontSize: '14px',
    lineHeight: '1.8',
  },
};

// ---------------------------------------------------------------------------
// Full-height Product Modal layout — issue #622, Sub-task 8.4. Always on;
// ProductModal.tsx composes these over the base `modalContentStyle`/
// `modalBodyStyle` rather than replacing the defaults here, since those
// base styles are still shared by every other modal in this directory.
// ---------------------------------------------------------------------------

/**
 * Full-height variant of `modalContentStyle`: fills the viewport height
 * (minus a fixed vertical margin) instead of sizing to content, and
 * switches to a flex column layout so the header/tabs/footer stay fixed
 * while `modalBodyStyleFullHeight` below is the one scrolling region — the
 * modal itself never grows unbounded regardless of tab content length.
 */
export const modalContentStyleFullHeight: React.CSSProperties = {
  ...modalStyles.modalContentStyle,
  width: '90vw', // Scales with viewport so wide tables (Associated Manufacturers/Products, incl. Product Type) always have room — only these full-height modals (Product, Manufacturer) need the extra width
  height: '90vh',
  display: 'flex',
  flexDirection: 'column',
  // The outer container no longer scrolls itself — modalBodyStyleFullHeight
  // is the single scrolling region so header/tabs/footer stay put.
  overflowY: 'hidden',
};

/**
 * Full-height variant of `modalBodyStyle`. `flex: 1` lets this region
 * absorb all remaining vertical space inside `modalContentStyleFullHeight`;
 * `minHeight: 0` is required alongside it — without it a flex child won't
 * shrink below its content's natural size, and the overflow would push the
 * outer container taller instead of scrolling internally.
 */
export const modalBodyStyleFullHeight: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  marginBottom: '24px',
  minHeight: 0,
};
